/* ==========================================================================
   video-hero.js — build item 2a
   --------------------------------------------------------------------------
   Owned by sections/video-hero.liquid. Loaded deferred by that section.

   WHAT THIS FILE IS FOR, AND WHAT IT IS DELIBERATELY NOT FOR

   Everything VISUAL in this section is already correct with JavaScript disabled:
   the cover crop, the scrim, the wordmark blend, the reduced-motion still and
   the mobile-video-off still are all CSS (the last two are emitted as a media
   query by snippets/kit-media-cover.liquid). This file only does the four things
   CSS cannot:

     1. AUTOPLAY SAFETY NET. `autoplay` is a request, not a guarantee. iOS Low
        Power Mode, data-saver modes and Safari's per-site autoplay setting all
        refuse it, and they refuse it silently — `play()` returns a rejected
        promise and nothing else happens. Unhandled, the merchant's homepage is
        a black rectangle and it gets reported as "the site is broken". So: call
        play(), swallow the rejection, and reveal the still.

     2. OFFSCREEN PAUSE. A decoding 6 MB loop nobody can see is a battery and
        CPU cost with no upside. IntersectionObserver, pause out / play in. This
        is explicitly NOT a visual effect and must never gate the poster.

     3. HONEST GATE BEHAVIOUR. The CSS gates HIDE the video; a `display: none`
        <video> can still buffer and decode. Under reduced motion or with mobile
        video off, this pauses it as well so the preference is honoured in
        bandwidth as well as in pixels (kit-media-cover NOTES 2).

     4. HEADER MEASUREMENT, for the `viewport_minus_header` height mode only.
        Measured rather than hardcoded, because the header's height depends on
        the merchant's logo size, menu length and announcement bar.

   NOT HERE, ON PURPOSE:
     · No reveal-on-scroll. This section is above the fold; revealing it would
       produce a blank first paint. assets/animations.js is never called.
     · No blend-mode scripting. The blend is CSS and has no JS half.
     · No player controls, no deferred-media click-to-play. Background scenery.
     · No scroll lock, no tab controller, no category bus — nothing in
       window.KitInteractions is relevant to this section, so nothing is called.

   THEME-EDITOR LIFECYCLE. `shopify:section:load` re-inits (the node is new).
   `shopify:section:select` re-issues play(): the editor re-parents the section
   element, which silently stops playback and leaves the merchant looking at a
   frozen frame while they edit. `shopify:section:deselect` does nothing, and
   `shopify:section:unload` tears the instance down so observers and matchMedia
   listeners do not leak across editor re-renders.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-video-hero]';
  var instances = new WeakMap();

  /* An attribute rendered from a Liquid boolean is the string "true"/"false",
     and an absent attribute must not read as false for a defaulted-on setting —
     so the default is passed in explicitly rather than inferred. */
  function readBool(el, name, fallback) {
    var raw = el.getAttribute(name);
    if (raw === null || raw === '') return fallback;
    return raw !== 'false' && raw !== '0';
  }

  function readInt(el, name, fallback) {
    var value = parseInt(el.getAttribute(name), 10);
    return isNaN(value) ? fallback : value;
  }

  /* addEventListener on a MediaQueryList is the modern API; addListener is the
     Safari < 14 spelling. Both are handled because the fallback of not handling
     them is a hero that stops responding to a rotation or a resize. */
  function onQueryChange(query, handler) {
    if (!query) return function () {};
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', handler);
      return function () {
        query.removeEventListener('change', handler);
      };
    }
    if (typeof query.addListener === 'function') {
      query.addListener(handler);
      return function () {
        query.removeListener(handler);
      };
    }
    return function () {};
  }

  function VideoHero(root) {
    this.root = root;
    this.teardown = [];

    this.autoplay = readBool(root, 'data-vh-autoplay', true);
    this.autoplayMobile = readBool(root, 'data-vh-autoplay-mobile', true);
    this.respectReducedMotion = readBool(root, 'data-vh-reduced-motion', true);
    this.pauseOffscreen = readBool(root, 'data-vh-pause-offscreen', true);
    this.mobileBreakpoint = readInt(root, 'data-vh-mobile-breakpoint', 749);
    this.measureHeader = root.hasAttribute('data-vh-measure-header');

    this.media = root.querySelector('.video-hero__media');
    /* data-kit-media is kit-media-cover's own hook. Matching on it rather than
       on `video` means an <iframe> embed is correctly ignored here: a
       cross-origin player cannot be paused from this side, and pretending
       otherwise would be a silent no-op that looks implemented. */
    this.video = root.querySelector('video[data-kit-media]');
    this.still = this.media ? this.media.querySelector('img') : null;

    this.isVisible = true;

    this.onIntersect = this.onIntersect.bind(this);
    this.onGateChange = this.onGateChange.bind(this);
    this.onCanPlay = this.onCanPlay.bind(this);
    this.measure = this.measure.bind(this);

    this.bindGates();
    this.bindHeader();
    this.bindVideo();
    this.bindVisibility();
    this.apply();
  }

  /* ---------------------------------------------------------------- gates */

  VideoHero.prototype.bindGates = function () {
    var mm = window.matchMedia;
    if (typeof mm !== 'function') return;

    if (this.respectReducedMotion) {
      this.reduceQuery = mm.call(window, '(prefers-reduced-motion: reduce)');
      this.teardown.push(onQueryChange(this.reduceQuery, this.onGateChange));
    }
    if (!this.autoplayMobile) {
      this.mobileQuery = mm.call(window, '(max-width: ' + this.mobileBreakpoint + 'px)');
      this.teardown.push(onQueryChange(this.mobileQuery, this.onGateChange));
    }
  };

  /* True when this visitor, at this viewport, is meant to see motion at all.
     Mirrors exactly the two conditions kit-media-cover's CSS gate uses, so the
     hidden state and the paused state can never disagree. */
  VideoHero.prototype.motionAllowed = function () {
    if (!this.autoplay) return false;
    if (this.reduceQuery && this.reduceQuery.matches) return false;
    if (this.mobileQuery && this.mobileQuery.matches) return false;
    return true;
  };

  VideoHero.prototype.onGateChange = function () {
    this.apply();
  };

  /* ---------------------------------------------------------------- video */

  VideoHero.prototype.bindVideo = function () {
    if (!this.video) return;
    var video = this.video;
    /* Both events, because which one fires first is engine- and
       cache-dependent, and a video already buffered when this script runs
       fires neither again. apply() is idempotent, so a double call is free. */
    video.addEventListener('loadeddata', this.onCanPlay);
    video.addEventListener('canplay', this.onCanPlay);
    this.teardown.push(function () {
      video.removeEventListener('loadeddata', this.onCanPlay);
      video.removeEventListener('canplay', this.onCanPlay);
    }.bind(this));
  };

  VideoHero.prototype.onCanPlay = function () {
    this.play();
  };

  VideoHero.prototype.play = function () {
    if (!this.video) return;
    if (!this.motionAllowed() || !this.isVisible) return;

    var self = this;
    var attempt = this.video.play();

    /* Chrome and Safari return a promise; older engines return undefined. A
       rejection here is the expected path on a locked-down device, not an
       error — so it is handled, never logged as a failure. */
    if (attempt && typeof attempt.catch === 'function') {
      attempt
        .then(function () {
          self.root.classList.remove('is-motion-blocked');
        })
        .catch(function () {
          self.revealStill();
        });
    }
  };

  /* The one rule this must never break: only hide the video when there is
     something behind it. kit-media-cover renders the still as a permanently
     visible layer BENEATH the motion layer, so hiding the motion layer reveals
     it with no second download and no state to keep in sync. With no still,
     hiding the video would leave the bare ground colour — a worse outcome than
     a frozen first frame, which at least shows the merchant's footage. */
  VideoHero.prototype.revealStill = function () {
    if (!this.still) return;
    this.root.classList.add('is-motion-blocked');
  };

  VideoHero.prototype.pause = function () {
    if (!this.video) return;
    try {
      this.video.pause();
    } catch (error) {
      /* A pause() during a pending load can throw in older WebKit. There is
         nothing to recover: the video is not playing either way. */
    }
  };

  /* Single source of truth for playback state. Called on init, on every gate
     change, on every intersection change and on editor select — so there is
     one code path deciding "playing or not" instead of four. */
  VideoHero.prototype.apply = function () {
    if (!this.video) return;

    if (!this.motionAllowed()) {
      /* Dropping the attribute matters as much as pausing: without it the
         engine may resume on its own after a source or visibility change, and
         a reduced-motion visitor would get motion back without asking. */
      this.video.removeAttribute('autoplay');
      this.video.autoplay = false;
      this.pause();
      /* Not is-motion-blocked: the CSS gate already hides the motion layer for
         these two cases, and adding the class would make an unrelated state
         (autoplay refused) indistinguishable from a respected preference. */
      return;
    }

    if (this.isVisible) {
      this.play();
    } else {
      this.pause();
    }
  };

  /* --------------------------------------------------------- visibility */

  VideoHero.prototype.bindVisibility = function () {
    if (!this.pauseOffscreen || !this.video) return;
    if (typeof window.IntersectionObserver !== 'function') return;

    /* threshold 0 with no rootMargin: the question is only "is any part of the
       stage on screen", which is exactly what a battery measure should ask.
       A larger threshold would pause a hero that is still half visible. */
    this.observer = new window.IntersectionObserver(this.onIntersect, {
      rootMargin: '0px',
      threshold: 0,
    });
    this.observer.observe(this.root);

    var self = this;
    this.teardown.push(function () {
      if (self.observer) {
        self.observer.disconnect();
        self.observer = null;
      }
    });
  };

  VideoHero.prototype.onIntersect = function (entries) {
    for (var i = 0; i < entries.length; i++) {
      this.isVisible = entries[i].isIntersecting;
    }
    this.apply();
  };

  /* -------------------------------------------------------------- header */

  VideoHero.prototype.bindHeader = function () {
    if (!this.measureHeader) return;

    /* In priority order, most specific first. The section group wrapper is the
       right answer when it exists because it includes the announcement bar; the
       others are fallbacks for a theme whose header is not in a group. */
    this.header =
      document.querySelector('.shopify-section-group-header-group') ||
      document.querySelector('#shopify-section-header') ||
      document.querySelector('sticky-header') ||
      document.querySelector('.header-wrapper');

    if (!this.header) return;

    this.measure();

    if (typeof window.ResizeObserver === 'function') {
      /* ResizeObserver rather than a resize listener: the header's height also
         changes without the window resizing — a webfont swap, a lazy logo, the
         announcement bar being dismissed — and each of those would otherwise
         leave the stage the wrong height until something else forced a reflow.
         It also self-debounces, which a resize listener does not. */
      this.resizeObserver = new window.ResizeObserver(this.measure);
      this.resizeObserver.observe(this.header);

      var self = this;
      this.teardown.push(function () {
        if (self.resizeObserver) {
          self.resizeObserver.disconnect();
          self.resizeObserver = null;
        }
      });
      return;
    }

    var frame = null;
    var self2 = this;
    var onResize = function () {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        self2.measure();
      });
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    this.teardown.push(function () {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (frame) window.cancelAnimationFrame(frame);
    });
  };

  VideoHero.prototype.measure = function () {
    if (!this.header) return;
    var height = Math.round(this.header.getBoundingClientRect().height);
    /* Written on the stage, not on :root — two heroes on one page (an inner
       page plus a hero in a template section) must not fight over one property,
       and a section-scoped custom property cannot leak into another section. */
    this.root.style.setProperty('--video-hero-header-h', height + 'px');
  };

  /* ------------------------------------------------------------ lifecycle */

  VideoHero.prototype.destroy = function () {
    for (var i = 0; i < this.teardown.length; i++) {
      try {
        this.teardown[i]();
      } catch (error) {
        /* A teardown that throws must not stop the remaining teardowns. */
      }
    }
    this.teardown = [];
    this.header = null;
  };

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = [];

    if (context.matches && context.matches(ROOT_SELECTOR)) {
      roots.push(context);
    }
    var found = context.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < found.length; i++) {
      roots.push(found[i]);
    }

    for (var j = 0; j < roots.length; j++) {
      var root = roots[j];
      if (instances.has(root)) continue;
      instances.set(root, new VideoHero(root));
    }
  }

  function destroy(scope) {
    if (!scope) return;
    var roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) {
      var found = scope.querySelectorAll(ROOT_SELECTOR);
      for (var i = 0; i < found.length; i++) roots.push(found[i]);
    }
    for (var j = 0; j < roots.length; j++) {
      var instance = instances.get(roots[j]);
      if (instance) {
        instance.destroy();
        instances.delete(roots[j]);
      }
    }
  }

  /* The script is deferred, so the DOM is parsed by the time it runs. The
     DOMContentLoaded guard is for the case where a merchant or an app moves the
     tag into <head> without defer. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    destroy(event.target);
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  /* Selecting a section makes the editor re-parent its node, which stops
     playback without firing any media event. Without this the merchant edits
     the hero while looking at a frozen frame and reasonably concludes the video
     setting is broken. */
  document.addEventListener('shopify:section:select', function (event) {
    var root = event.target.matches && event.target.matches(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector && event.target.querySelector(ROOT_SELECTOR);
    if (!root) return;
    var instance = instances.get(root);
    if (instance) {
      instance.isVisible = true;
      instance.apply();
    } else {
      init(event.target);
    }
  });

  /* Exposed for debugging and for any future section that needs to re-measure
     after changing the header. Not a shared API: kit-interactions.js owns the
     published surface, and this deliberately does not add to it. */
  window.VideoHero = { init: init, destroy: destroy };
})();
